/**
 * Core configuration constants for VerifyPDF
 * 
 * This file contains:
 * - Pricing tier definitions (features, prices, descriptions)
 * - Frequently Asked Questions with answers
 * - Personal email domain lists for business email validation
 * - Logo configurations
 * 
 * Note: Content is now managed through i18n translations
 */

export const tiers = [
  {
    id: 'tier-basic',
    href: 'https://secure.verifypdf.com/signup',
    featured: false,
    translationKey: 'basic'
  },
  {
    id: 'tier-professional',
    href: 'https://secure.verifypdf.com/signup',
    featured: true,
    translationKey: 'professional'
  },
  {
    id: 'tier-corporate',
    href: '/contact',
    featured: false,
    translationKey: 'corporate'
  }
];

// FAQs are now loaded directly from translations
export const getFAQs = (t) => {
  return t('faq.items', { returnObjects: true });
};

// Helper to get pricing tier data
export const getTierData = (tier, t) => {
  try {
    const data = t(`pricing.tiers.${tier.translationKey}`, { returnObjects: true });
    if (!data) {
      console.warn(`No translation data found for tier: ${tier.translationKey}`);
      return {
        ...tier,
        name: '',
        priceMonthly: '',
        description: '',
        features: []
      };
    }
    return {
      ...tier,
      name: data.name || '',
      priceMonthly: data.price || '',
      description: data.description || '',
      features: Array.isArray(data.features) ? data.features : []
    };
  } catch (error) {
    console.error(`Error loading tier data for ${tier.translationKey}:`, error);
    return {
      ...tier,
      name: '',
      priceMonthly: '',
      description: '',
      features: []
    };
  }
};

// Personal email domains for business email validation
export const personalDomains = [
  // Free email providers
  'gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'gmx.com',
  'gmx.net', 'protonmail.com', 'aol.com', 'icloud.com', 'mail.com',
  'zoho.com', 'yandex.com', 'tutanota.com', 'fastmail.com', 'hey.com',
  'pm.me', 'live.com', 'yahoo.co.uk', 'yahoo.fr', 'yahoo.de',
  'yahoo.it', 'yahoo.es', 'yahoo.co.jp', 'msn.com', 'me.com',
  'mac.com', 'googlemail.com', 'web.de', 'yomail.edu.pl',
  // Netherlands ISPs
  'ziggo.nl', 'kpnmail.nl', 'xs4all.nl',
  // UK ISPs
  'btinternet.com', 'sky.com', 'virginmedia.com', 'talktalk.net',
  'plusnet.com', 'aol.co.uk', 'ntlworld.com', 'tiscali.co.uk',
  'ee.co.uk', 'three.co.uk', 'o2.co.uk',
  // US ISPs
  'verizon.net', 'att.net', 'comcast.net', 'cox.net', 'charter.net',
  'optimum.net', 'frontier.com', 'spectrum.net', 'suddenlink.net',
  // Canadian ISPs
  'rogers.com', 'shaw.ca', 'bell.net', 'telus.net', 'videotron.ca',
  'eastlink.ca',
  // Australian ISPs
  'bigpond.com', 'optusnet.com.au', 'iinet.net.au', 'tpg.com.au',
  'internode.on.net', 'telstra.com', 'dodo.com.au', 'adam.com.au',
  'westnet.com.au', 'ozemail.com.au',
  // German ISPs
  't-online.de', 'vodafone.de', 'arcor.de', 'kabel.de', 'unitymedia.de',
  '1und1.de', 'freenet.de', 'o2online.de', 'alice-dsl.de',
  'ewe.net', 'kabeldeutschland.de',
  // French ISPs
  'orange.fr', 'sfr.fr', 'free.fr', 'numericable.fr', 'bouygues.fr',
  'wanadoo.fr', 'neuf.fr', 'club-internet.fr', 'laposte.net',
  'bbox.fr', 'aliceadsl.fr',
  // Italian ISPs
  'tim.it', 'alice.it', 'libero.it', 'fastweb.it', 'tiscali.it',
  'virgilio.it', 'tin.it', 'vodafone.it', 'wind.it', 'poste.it',
  'aruba.it',
  // Spanish ISPs
  'movistar.es', 'orange.es', 'vodafone.es', 'jazztel.es', 'ono.es',
  'telefonica.net', 'ya.com', 'euskaltel.es', 'telecable.es',
  'hotmail.es', 'terra.es',
  // Other
  'freemail.hu', 'inbox.com', 'mail.ru', 'mozmail.com',
  // Additional domains from original list
  'daum.net', 'hanmail.net', 'naver.com', 'qq.com', 'sina.com',
  'foxmail.com', 'rediffmail.com', 'rambler.ru', 'yandex.ru',
  'onmicrosoft.com', 'mailinator.com', 'tempmail.com'
];

// Logo names for trusted by section
export const LOGO_NAMES = [
  '4bimmo', 'adecco', 'amazon', 'bajajfinserv', 'centralpay',
  'cloudtalk', 'curelink', 'digiverifier', 'ethena', 'ey',
  'hartehanks', 'inmoprocesos', 'kerala', 'kinetix', 'montium',
  'naver', 'navi', 'nbx', 'networkfunding', 'paypro',
  'priya', 'quantum', 'recsol', 'speedtrader', 'tic',
  'vericasa', 'wns'
]; 